<template>
  <div ref="rootRef" />
</template>

<script setup lang="ts">
import 'ckeditor5/ckeditor5.css'

import * as Sentry from '@sentry/nuxt'
import { useDebounceFn } from '@vueuse/core'
import type { InlineEditor } from 'ckeditor5'
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { getErrorContext } from '@/helpers/errors'

import { useI18n } from '#imports'
import { useNotificationStore } from '~/store/notification'

const INPUT_EVENT_DEBOUNCE_WAIT = 300

interface TextEditorProps {
  placeholder?: string
}

const model = defineModel<string>()

const props = defineProps<TextEditorProps>()

const { t } = useI18n()
const store = useNotificationStore()

const rootRef = ref<HTMLDivElement | null>(null)

let editor: InlineEditor | null = null

const debouncedInputFn = useDebounceFn((newValue) => {
  model.value = newValue
}, INPUT_EVENT_DEBOUNCE_WAIT)

onMounted(async () => {
  if (import.meta.server || !rootRef.value) return

  try {
    const { InlineEditor, Autoformat, Bold, Essentials, Heading, Italic, Link, List, Paragraph, PasteFromOffice, TextTransformation } =
      await import('ckeditor5')

    editor = await InlineEditor.create(rootRef.value, {
      licenseKey: 'GPL',
      plugins: [Essentials, Autoformat, Bold, Italic, Heading, Link, List, Paragraph, PasteFromOffice, TextTransformation],
      toolbar: {
        items: ['heading', '|', 'bold', 'italic', '|', 'bulletedList', 'numberedList', '|', 'link'],
      },
      heading: {
        options: [
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1',
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2',
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3',
          },
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph',
          },
        ],
      },
      placeholder: props.placeholder,
      initialData: model.value,
    })

    editor.model.document.on('change:data', () => debouncedInputFn(editor?.getData()))
  } catch (error: unknown) {
    Sentry.captureException(error, getErrorContext())

    store.add({
      type: 'error',
      message: t('errors.editor'),
    })
  }
})

onBeforeUnmount(() => {
  editor?.destroy()
  editor = null
})
</script>
